import { Controller } from "stimulus"
import { getCookieWithConsent, setCookieWithConsent } from "../utils/cookies"
import { Consent } from "../utils/consent"
import { getLocalStorageWithConsent, setLocalStorageWithConsent } from "../utils/storage"
import ga from "../utils/ga"

export default class extends Controller {
  static values = {
    url: String,
    data: Object,
    category: String,
  }

  static targets = ["css", "html", "toast"]

  static classes = ["dismiss"]

  connect() {
    this.pickAndDisplayBanner(this.dataValue.banners)
    this.reportHeaderBannerImpression()
    this.setToastHrefs()
  }

  click(event) {
    const clickedDismissOrChild = Boolean(event.target.closest(".js-banner__dismiss"))
    if (clickedDismissOrChild) {
      event.preventDefault()
      this.onDismiss()
    } else {
      if (this.isToastBannerActive() && event.target.tagName !== "A") {
        return
      }
      this.reportHeaderBannerClick()
    }
  }

  onDismiss() {
    this.hideBanner()
    if (this.banner) {
      setLocalStorageWithConsent(this.getBannerCacheId(this.banner), "hidden", Consent.marketing)
    }
    this.reportHeaderBannerClose()
  }

  pickAndDisplayBanner(banners) {
    this.banner = this.pickBanner(banners)
    if (this.banner) {
      if (this.hasHtmlTarget) {
        this.htmlTarget.innerHTML = this.banner.html
      }
      this.cssTarget.innerHTML = this.banner.css
      this.showBanner()
    } else {
      this.hideBanner()
    }
  }

  hideBanner() {
    this.element.classList.remove(this.element.dataset.bannerShowClass)
  }

  showBanner() {
    this.element.classList.add(this.element.dataset.bannerShowClass)
  }

  pickBanner(banners) {
    if (banners && banners.length > 0) {
      const pageKey = this.getPageKey()
      const banner = this.getPreviouslyPickedBanner(banners, pageKey) || this.pickRandomBanner(banners, pageKey)
      const selectedBanner = this.isDismissed(banner) ? null : banner

      return selectedBanner
    }
    return null
  }

  getPageKey() {
    return this.categoryValue || "global"
  }

  globalBanners(banners) {
    return banners.filter((banner) => banner.categories === undefined || banner.categories.length === 0)
  }

  getPreviouslyPickedBanner(nonDismissedBanners, pageKey) {
    const previouslyPickedBannerId = this.getPreviouslyPickedBannerIds()[pageKey]
    return nonDismissedBanners.find((banner) => banner.id === previouslyPickedBannerId)
  }

  pickRandomBanner(banners, pageKey) {
    let filteredBanners
    if (pageKey === "global") {
      filteredBanners = this.globalBanners(banners)
    } else {
      filteredBanners = banners.filter((banner) => banner.categories && banner.categories.includes(pageKey))
      if (filteredBanners.length === 0) {
        filteredBanners = this.globalBanners(banners)
      }
    }
    const indexPicked = Math.floor(Math.random() * filteredBanners.length)
    const pickedBanner = filteredBanners[indexPicked]
    if (pickedBanner) {
      this.addCachedBannerId(pageKey, pickedBanner.id)
    }

    return pickedBanner
  }

  getPreviouslyPickedBannerIds() {
    const ids = getCookieWithConsent("marketplace_banners", Consent.marketing, JSON.stringify({}))
    return ids ? JSON.parse(ids) : {}
  }

  addCachedBannerId(pageKey, bannerId) {
    setCookieWithConsent(
      "marketplace_banners",
      JSON.stringify({
        ...this.getPreviouslyPickedBannerIds(),
        [pageKey]: bannerId,
      }),
      Consent.marketing
    )
  }

  getBannerCacheId(banner) {
    if (banner) {
      return `banner_${banner.id}_cache`
    }
    return null
  }

  isDismissed(banner) {
    return getLocalStorageWithConsent(this.getBannerCacheId(banner), Consent.marketing) === "hidden"
  }

  reportHeaderBannerClick() {
    if (this.banner) {
      ga("ec:addPromo", this.bannerPayload())
      ga("ec:setAction", "promo_click")
      ga("send", "event", "Internal Promotions", "click", {
        label: this.promotionNameForGaEvents(),
      })
    }
  }

  reportHeaderBannerClose() {
    if (this.banner) {
      ga("ec:addPromo", this.bannerPayload())
      ga("ec:setAction", "promo_click")
      ga("send", "event", this.impressionCategoryForGaEvents(), "close", {
        label: this.promotionNameForGaEvents(),
      })
    }
  }

  reportHeaderBannerImpression() {
    if (this.banner && !this.isBannerHiddenViaExperiment()) {
      ga("ec:addPromo", this.bannerPayload())
      ga("send", "event", this.impressionCategoryForGaEvents(), "view", {
        id: this.promotionNameForGaEvents(),
        label: this.promotionNameForGaEvents(),
      })
    }
  }

  reportFooterSlideBannerShow() {
    ga("send", "event", "Banner Impression", "Show-scrollup")
  }

  reportFooterSlideBannerDismiss() {
    ga("send", "event", "Banner Impression", "Dismissed-scrolldown")
  }

  bannerPayload() {
    return { id: this.promotionNameForGaEvents(), name: this.promotionNameForGaEvents() }
  }

  onScroll() {
    // We have a banner experiment running where we want to animate the banner in/out on scroll events.
    // We only want to do this when the "marketingBannerFooterSlide" experiment toggle is `true`,
    // and when the banner is visible (i.e. the parent element has the `bannerShowClass` applied).
    if (
      this.element.classList.contains(this.element.dataset.bannerShowClass) &&
      this.hasHtmlTarget &&
      // spellr:disable-next
      this.htmlTarget.dataset.experimentToggle === "marketingBannerFooterSlide"
    ) {
      // Banner is visible and in the right "slide" experiment variant, so we do our magic:
      window.requestAnimationFrame(this.animateBannerOnScroll.bind(this))
    }
  }

  animateBannerOnScroll() {
    const currentScrollPosition = window.scrollY
    if (!this.previousScrollPosition) {
      this.previousScrollPosition = currentScrollPosition
      return
    }
    if (currentScrollPosition > this.previousScrollPosition + 50) {
      // user has scrolled down 50px
      if (this.element.dataset.marketingBannerSlideHidden !== "true") this.reportFooterSlideBannerDismiss()
      this.element.setAttribute("data-marketing-banner-slide-hidden", "true")
      this.previousScrollPosition = currentScrollPosition
    } else if (currentScrollPosition < this.previousScrollPosition - 50) {
      // user has scrolled up 50px
      if (this.element.dataset.marketingBannerSlideHidden === "true") this.reportFooterSlideBannerShow()
      this.element.removeAttribute("data-marketing-banner-slide-hidden")
      this.previousScrollPosition = currentScrollPosition
    }
  }

  setToastHrefs() {
    if (!this.hasToastTarget) return

    const originalBannerHref = this.getOriginalBannerHref()
    if (!originalBannerHref) return

    const toastHref = this.applyToastUtmParamsToUrl(originalBannerHref)

    for (const toastLink of this.toastTarget.querySelectorAll("a")) {
      toastLink.href = toastHref
    }
  }

  getOriginalBannerHref() {
    if (!this.banner) return null

    const hrefRegexMatch = this.banner.html.replace(/&amp;/g, "&").match(/href="([^"]+)"/)
    if (!hrefRegexMatch) return null

    return hrefRegexMatch[1]
  }

  applyToastUtmParamsToUrl(originalBannerHref) {
    return originalBannerHref
      .split(/(\?|&)/)
      .map((segment) => {
        if (segment.includes("utm_campaign")) return segment.replace("mkt-header", "mkt-toast")
        if (segment.includes("utm_content")) return segment.replace("header-bar", "toast")
        return segment
      })
      .join("")
  }

  impressionCategoryForGaEvents() {
    if (this.isToastBannerActive()) {
      return "Toast Impression"
    } else {
      return "Banner Impression"
    }
  }

  promotionNameForGaEvents() {
    if (this.isToastBannerActive()) {
      return this.banner.id.replace("mkt-header", "mkt-toast")
    } else {
      return this.banner.id
    }
  }

  isToastBannerActive() {
    return this.hasToastTarget
  }

  isBannerHiddenViaExperiment() {
    return !this.hasHtmlTarget && !this.hasToastTarget
  }
}
